'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ImageFeedback, FeedbackType, ImageType } from '@/lib/types';
import api from '@/lib/api';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import { LazyImage } from '@/components/LazyImage';

export default function FeedbackManagementPage() {
  const [items, setItems] = useState<ImageFeedback[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [feedbackFilter, setFeedbackFilter] = useState<FeedbackType | ''>('');
  const [imageTypeFilter, setImageTypeFilter] = useState<ImageType | ''>('');

  // Pagination
  const [page, setPage] = useState(0);
  const limit = 50;

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const toast = useToast();
  const { confirm } = useConfirm();

  const fetchFeedback = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.listFeedback({
        feedback: feedbackFilter || undefined,
        image_type: imageTypeFilter || undefined,
        limit,
        offset: page * limit,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [feedbackFilter, imageTypeFilter, page]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
  }, [feedbackFilter, imageTypeFilter]);

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  const handleRemoveFeedback = async (imagePath: string) => {
    try {
      await api.removeFeedback(imagePath);
      toast.success('Feedback removed');
      fetchFeedback();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleBatchDeleteDisliked = async (imageType?: ImageType) => {
    const typeLabel = imageType === 'generated' ? 'generated images' : imageType === 'preview' ? 'preview images' : 'all images';
    const confirmed = await confirm({
      title: 'Delete Disliked Images',
      message: `This will permanently delete all disliked ${typeLabel} and their files. This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      const result = await api.batchDeleteDisliked(imageType);
      toast.success(`Deleted ${result.deleted_count} images`);
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} errors occurred`);
      }
      fetchFeedback();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleBatchClearFeedback = async () => {
    const confirmed = await confirm({
      title: 'Clear All Feedback',
      message: 'This will remove all feedback records without deleting images. Continue?',
      confirmText: 'Clear',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      const result = await api.batchClearFeedback({
        feedback: feedbackFilter || undefined,
        image_type: imageTypeFilter || undefined,
      });
      toast.success(`Cleared ${result.cleared_count} feedback records`);
      fetchFeedback();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[var(--bg-surface)]/95 backdrop-blur-sm border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                href="/"
                className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </Link>
              <h1 className="text-xl font-semibold">Image Feedback</h1>
              <span className="text-[var(--text-muted)] text-sm">
                {total} {total === 1 ? 'item' : 'items'}
              </span>
            </div>

            {/* Actions dropdown */}
            <div className="flex items-center gap-2">
              <select
                value={feedbackFilter}
                onChange={(e) => setFeedbackFilter(e.target.value as FeedbackType | '')}
                className="px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg text-sm focus:border-[var(--accent)] outline-none cursor-pointer"
              >
                <option value="">All Feedback</option>
                <option value="like">Liked</option>
                <option value="dislike">Disliked</option>
              </select>

              <select
                value={imageTypeFilter}
                onChange={(e) => setImageTypeFilter(e.target.value as ImageType | '')}
                className="px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg text-sm focus:border-[var(--accent)] outline-none cursor-pointer"
              >
                <option value="">All Types</option>
                <option value="generated">Generated</option>
                <option value="preview">Preview</option>
              </select>

              {/* Batch actions */}
              <div className="relative group">
                <button className="px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg text-sm hover:bg-[var(--bg-hover)] transition-colors cursor-pointer flex items-center gap-2">
                  Actions
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute right-0 mt-1 w-56 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <button
                    onClick={() => handleBatchDeleteDisliked()}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                  >
                    Delete all disliked images
                  </button>
                  <button
                    onClick={() => handleBatchDeleteDisliked('generated')}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                  >
                    Delete disliked generated
                  </button>
                  <button
                    onClick={() => handleBatchDeleteDisliked('preview')}
                    className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                  >
                    Delete disliked previews
                  </button>
                  <div className="border-t border-[var(--border-subtle)] my-1" />
                  <button
                    onClick={handleBatchClearFeedback}
                    className="w-full px-4 py-2 text-left text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                  >
                    Clear feedback records
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[var(--text-muted)]">
            <svg className="w-16 h-16 opacity-30 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <p className="text-lg">No feedback found</p>
            <p className="text-sm mt-1">Like or dislike images to see them here</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-[var(--bg-elevated)]">
                  <tr>
                    <th className="w-12 px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === items.length && items.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-[var(--border-subtle)] cursor-pointer"
                      />
                    </th>
                    <th className="w-20 px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                      Feedback
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                      Date
                    </th>
                    <th className="w-24 px-4 py-3 text-right text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className={`hover:bg-[var(--bg-elevated)]/50 transition-colors ${
                        selectedIds.has(item.id) ? 'bg-[var(--accent)]/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleSelection(item.id)}
                          className="w-4 h-4 rounded border-[var(--border-subtle)] cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <LazyImage
                          src={api.getThumbnailUrl(item.image_path)}
                          fallbackSrc={api.getImageUrl(item.image_path)}
                          alt=""
                          className="w-12 h-12 rounded-lg overflow-hidden"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            item.image_type === 'generated'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400'
                          }`}
                        >
                          {item.image_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                        {item.image_type === 'generated' && item.job_id ? (
                          <span className="font-mono text-xs">{item.job_id.slice(0, 8)}...</span>
                        ) : item.image_type === 'preview' && item.list_id ? (
                          <span>List #{item.list_id}</span>
                        ) : (
                          <span className="text-[var(--text-muted)]">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            item.feedback === 'like'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {item.feedback === 'like' ? (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                            </svg>
                          )}
                          {item.feedback}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--text-muted)]">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleRemoveFeedback(item.image_path)}
                          className="p-1.5 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors cursor-pointer"
                          title="Remove feedback"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-[var(--text-muted)]">
                  Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg text-sm hover:bg-[var(--bg-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-[var(--text-muted)]">
                    Page {page + 1} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg text-sm hover:bg-[var(--bg-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
